import React, { useState } from 'react'
import Dropzone from 'react-dropzone'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid,
} from '../../../../components/mui/Core/index'
import { styled, useTheme } from '../../../../components/mui/Styles/index'
import { FileUploadIcon, DescriptionIcon } from '../../../../components/mui/Icons'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import SubHeader from '../../../../components/SubHeader/sub-header'
import Constants from '../../../../constants/en.json'
import {
  fetchOrderDocumentsByOrderId,
  fetchOrderDocumentTypes,
  getUrlForUploadOrderDocument,
  postOrderDocumentType,
  saveDocumentUploadRecord,
} from '../../OrderDetails/order-document-slice'
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_LIMIT,
} from '../../../../constants/document-upload-validations'

const CenteredContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
}))

function UploadDocumentModal({ isOpen, handleClose, orderDetails }) {
  const theme = useTheme()
  const [documentType, setDocumentType] = useState('')
  const [otherDocumentType, setOtherDocumentType] = useState('')
  const [file, setFile] = useState(null)
  const [rejectedFileName, setRejectedFileName] = useState('')
  const [showError, setShowError] = useState('')
  const [isOtherDocumentTypeExists, setIsOtherDocumentTypeExists] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const dispatch = useDispatch()

  const orderDocument = useSelector((state) => state.orderDocument)
  const selectedOrder = useSelector((state) => state.order.selectedOrder)
  const selectedShipment = useSelector((state) => state.orderList.selectedShipment)
  const uploadOrderDocumentTypesError = useSelector(
    (state) => state.orderDocument.uploadOrderDocumentTypesError,
  )
  const uploadOrderDocumentError = useSelector(
    (state) => state.orderDocument.uploadOrderDocumentError,
  )
  const recordSaveError = useSelector((state) => state.orderDocument.recordSaveError)

  const orderDocumentTypes = orderDocument?.orderDocumentTypes?.documentTypes || []
  const orderDocumentTypesList = [...orderDocumentTypes, 'Other']

  const handleDocumentTypeChange = (event) => {
    const selectedItem = orderDocumentTypesList?.find((item) => item === event.target.value)
    setDocumentType(selectedItem)
    setOtherDocumentType('')
    setIsOtherDocumentTypeExists(false)
  }
  const handleOtherDocumentTypeChange = (event) => {
    const typedValue = event.target.value
    setOtherDocumentType(typedValue)
    const exists = orderDocumentTypesList?.some(
      (item) => item.trim().toLowerCase() === typedValue.trim().toLowerCase(),
    )
    if (exists) setIsOtherDocumentTypeExists(true)
    else setIsOtherDocumentTypeExists(false)
  }
  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      setFile(acceptedFiles[0])
      setRejectedFileName('')
      setShowError('')
    }
  }
  const handleFileReject = (fileRejections) => {
    if (fileRejections?.length) {
      setFile(null)
      setRejectedFileName(fileRejections[0]?.file?.name)
      setShowError(fileRejections.at(0).errors.at(0).code)
    }
  }
  const handleModalClose = () => {
    handleClose()
    setDocumentType('')
    setFile(null)
    setShowError('')
    setRejectedFileName('')
    setOtherDocumentType('')
    setIsOtherDocumentTypeExists(false)
    setIsUploading(false)
  }

  const handleUpload = async () => {
    if (!documentType || !file) return

    setIsUploading(true)
    let finalDocumentType = documentType

    try {
      if (documentType === 'Other') {
        finalDocumentType = otherDocumentType?.trim()
        const result = await dispatch(postOrderDocumentType(finalDocumentType))
        if (result.error) throw new Error(result.error.message || 'Failed to create document type')
      }

      const orderDocumentData = {
        orderId: orderDetails?.wmsOrderNumber,
        file: file?.name,
        documentType: finalDocumentType,
        contentType: file?.type,
      }

      const getUrlResponse = await dispatch(
        getUrlForUploadOrderDocument({
          params: orderDocumentData,
        }),
      )

      if (getUrlResponse.error) {
        throw new Error(getUrlResponse.error.message || 'Failed to get upload URL')
      }

      const { uploadUrl } = getUrlResponse.payload
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      })
      if (!uploadResponse.ok) {
        throw new Error(uploadResponse.statusText || 'Upload failed')
      }
      if (uploadResponse?.status === 200) {
        const recordRequest = {
          filePath: getUrlResponse?.payload?.objectKey,
          fileName: file.name,
          orderId: orderDetails?.wmsOrderNumber,
          documentType: finalDocumentType,
        }
        const recordResponse = await dispatch(saveDocumentUploadRecord(recordRequest))
        if (recordResponse.error) {
          throw new Error(recordResponse.error.message || 'Failed to save upload record')
        }

        dispatch(
          setSnackbar({
            message: Constants.model.documentUpload.DOCUMENT_UPLOAD_SUCCESS.replace(
              '__DOCUMENT_TYPE__',
              finalDocumentType,
            ),
            severity: 'success',
            action: true,
            autoHideDuration: 2000,
          }),
        )
      }

      const typesAction = await dispatch(fetchOrderDocumentTypes())

      const orderId = selectedShipment?.wmsOrderNum || selectedOrder?.wms_order
      if (orderId) {
        dispatch(fetchOrderDocumentsByOrderId(orderId))
      }

      if (
        uploadOrderDocumentTypesError ||
        uploadOrderDocumentError ||
        typesAction.error ||
        recordSaveError
      ) {
        dispatch(
          setSnackbar({
            message:
              typesAction.error?.message ||
              uploadOrderDocumentError?.message ||
              uploadOrderDocumentTypesError?.message ||
              recordSaveError?.message,
            severity: 'error',
            action: true,
            autoHideDuration: 2000,
          }),
        )
        return
      }

      handleModalClose()
    } catch (exception) {
      dispatch(
        setSnackbar({
          message: exception?.message || 'An error occurred during upload',
          severity: 'error',
          action: true,
          autoHideDuration: 2000,
        }),
      )
    } finally {
      setIsUploading(false)
    }
  }

  const isSubmitDisabled =
    !documentType ||
    !file ||
    showError ||
    isOtherDocumentTypeExists ||
    (documentType === 'Other' && !otherDocumentType?.trim()) ||
    isUploading

  const containerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '496px',
    bgcolor: theme.palette.common.white,
    boxShadow: 24,
    padding: '10px 20px 10px 25px',
    border: 'none !important',
    borderRadius: '12px',
  }
  const cardBodyStyle = {
    paddingTop: '15px',
    color: theme.palette.common.black,
    fontFamily: 'AtlasGroteskRegular',
    fontSize: '16px',
    lineHeight: '20px',
    marginBottom: '10px',
  }
  const buttonsContainerStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px',
  }
  const modalLabelStyle = {
    marginTop: '8px',
  }
  const modalInputStyle = {
    width: '203px',
    marginTop: '8px',
    borderRadius: '4px',
  }
  const otherDocumentTypeStyle = {
    p: 0,
    m: 2,
    '& .MuiInputLabel-root': {
      color: isOtherDocumentTypeExists ? theme.palette.error.main : theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: isOtherDocumentTypeExists ? theme.palette.error.main : theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 2,
      borderColor: isOtherDocumentTypeExists ? theme.palette.error.main : theme.palette.grey[700],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: isOtherDocumentTypeExists
        ? theme.palette.error.main
        : theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: isOtherDocumentTypeExists
        ? theme.palette.error.main
        : theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-input': {
      color: isOtherDocumentTypeExists ? theme.palette.error.main : theme.palette.text.primary,
    },
  }
  const iconStyle = {
    fontSize: 30,
    mb: 1,
  }
  const smallSpacing = {
    mb: 5,
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <CenteredContainer>
        <Box sx={containerStyle}>
          <SubHeader title={Constants.model.documentUpload.UPLOAD_DOCUMENT} />
          <Typography sx={cardBodyStyle}>
            {Constants.model.documentUpload.SELECT_DOCUMENT_TYPE}
          </Typography>

          <FormControl sx={smallSpacing}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel sx={modalLabelStyle}>
                  {Constants.model.documentUpload.DOCUMENT}
                </InputLabel>
                <Select
                  name={documentType || ''}
                  value={documentType || ''}
                  onChange={handleDocumentTypeChange}
                  sx={modalInputStyle}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 180,
                        overflowY: 'auto',
                        marginTop: 5,
                      },
                    },
                  }}
                >
                  {orderDocumentTypesList?.map((item) => {
                    return (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    )
                  })}
                </Select>
              </Grid>
              {documentType === 'Other' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    type='text'
                    fullWidth
                    value={otherDocumentType}
                    onChange={handleOtherDocumentTypeChange}
                    label={Constants.model.documentUpload.OTHER_DOCUMENT_TYPE}
                    sx={otherDocumentTypeStyle}
                  />
                </Grid>
              )}
            </Grid>
            {isOtherDocumentTypeExists && (
              <Grid size={{ xs: 12 }}>
                <Typography color='error' sx={{ mt: 1 }}>
                  {Constants.model.documentUpload.DOCUMENT_TYPE_ALREADY_EXISTS.replace(
                    '__DOCUMENT_TYPE__',
                    otherDocumentType,
                  )}
                </Typography>
              </Grid>
            )}
          </FormControl>

          <Box sx={smallSpacing}>
            <Dropzone
              onDrop={(acceptedFiles) => handleFileDrop(acceptedFiles)}
              onDropRejected={handleFileReject}
              accept={ACCEPTED_FILE_TYPES}
              multiple={false}
              maxSize={MAX_FILE_SIZE_LIMIT}
            >
              {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => {
                let DynamicIconComponent = FileUploadIcon
                let dynamicBorderColor = theme.palette.grey[900]
                let dynamicTextColor = theme.palette.grey[900]
                let dynamiciconColor = theme.palette.grey[900]
                const dropzoneContainerStyle = {
                  p: 4,
                  borderRadius: 2,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '1px dashed',
                  transition: 'all 0.2s ease-in-out',
                  borderColor: dynamicBorderColor,
                  bgcolor: isDragActive ? 'action.hover' : theme.palette.grey[400],
                }

                if (isDragActive) setShowError('')

                if (isDragReject) {
                  dynamicBorderColor = theme.palette.error.main
                  dynamicTextColor = theme.palette.error.main
                } else if (isDragActive || file) {
                  dynamicBorderColor = theme.palette.primary.main
                  dynamicTextColor = theme.palette.primary.main
                }

                if (isDragReject || showError) {
                  DynamicIconComponent = DescriptionIcon
                  dynamiciconColor = theme.palette.error.main
                } else if (file) {
                  DynamicIconComponent = DescriptionIcon
                  dynamiciconColor = theme.palette.primary.main
                } else if (isDragActive || isDragAccept) {
                  DynamicIconComponent = DescriptionIcon
                  dynamiciconColor = theme.palette.primary.main
                }
                const dragableStateText = isDragActive
                  ? Constants.model.documentUpload.DROP_FILE_HERE
                  : rejectedFileName || file?.name || Constants.model.documentUpload.CHOOSE_FILE

                return (
                  <Box
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getRootProps()}
                    sx={dropzoneContainerStyle}
                  >
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <input {...getInputProps()} />

                    <DynamicIconComponent
                      sx={{
                        ...iconStyle,
                        color: dynamiciconColor,
                      }}
                    />

                    <Typography
                      variant='body1'
                      sx={{
                        color: dynamicTextColor,
                      }}
                    >
                      {dragableStateText}
                    </Typography>
                  </Box>
                )
              }}
            </Dropzone>

            <Box sx={{ mt: 4 }}>
              <Typography variant='body1' color={showError === 'file-too-large' ? 'error' : 'text'}>
                {Constants.model.documentUpload.FILE_SIZE_VALIDATION_MESSAGE}
              </Typography>

              <Typography
                variant='body1'
                color={showError === 'file-invalid-type' ? 'error' : 'text'}
                sx={{ mt: 1 }}
              >
                {Constants.model.documentUpload.FILE_TYPE_VALIDATION_MESSAGE}
              </Typography>
            </Box>
          </Box>

          <Box sx={buttonsContainerStyle}>
            <Button variant='outlined' color='secondary' onClick={handleModalClose}>
              {Constants.model.CANCEL}
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={handleUpload}
              disabled={isSubmitDisabled}
            >
              {isUploading ? Constants.model.UPLOADING : Constants.model.UPLOAD}
            </Button>
          </Box>
        </Box>
      </CenteredContainer>
    </Modal>
  )
}

UploadDocumentModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  orderDetails: PropTypes.any.isRequired,
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default UploadDocumentModal
